import type { AppState, BirthDetailsInput, BirthMeridiem, BirthTimeStatus, PersonalizationGender } from '../types';
import { birthDateSchema, birthDetailsSchema, isBirthDateStepValid, isBirthDetailsStepValid, nameSchema, profileInputSchema } from '../modules/validation';
import { element, registerCleanup } from './dom';
import { birthDateSelector } from './birth-date-selector';
import { zodiacHeader } from './zodiac-header';
import { questionProgress, type QuestionProgressStep } from './question-progress';

export interface PersonalDetailsInput {
  first_name: string;
  gender_personalization: PersonalizationGender;
  birth_date: string;
  birth_time_status: BirthTimeStatus;
  birth_hour?: number;
  birth_minute?: number;
  birth_meridiem?: BirthMeridiem;
  birthplace?: string;
}

export function prefillDetailsMessage(firstName: string, hasBirthDate: boolean): string {
  return hasBirthDate ? `Welcome, ${firstName}. Your birth date is ready to confirm or edit.` : `Welcome, ${firstName}. When were you born?`;
}

function questionShell(id: string, title: string, subtitle: string, step: QuestionProgressStep = 'birth_date'): { section: HTMLElement; shell: HTMLElement } {
  const section = element('section', { className: 'screen funnel', attrs: { 'aria-labelledby': id } });
  section.dataset.celestialMode = 'funnel';
  const shell = element('div', { className: 'shell funnel-card' });
  shell.append(
    zodiacHeader(section, true),
    questionProgress(step),
    element('p', { className: 'question-label', text: 'Question 1 of 2' }),
    element('h1', { className: 'title', text: title, attrs: { id } }),
    element('p', { className: 'question-sub', text: subtitle }),
  );
  section.append(shell);
  return { section, shell };
}

function backButton(label: string, onBack: () => void): HTMLButtonElement {
  return element('button', { className: 'text-button return-button', text: `← ${label}`, attrs: { type: 'button' }, on: { click: onBack } });
}

function stepError(step: string): HTMLElement {
  return element('p', { className: 'error step-error', attrs: { id:`${step}-error`,role: 'alert', 'data-validation-step': step } });
}

export function birthDateView(state: AppState, onSubmit: (details: PersonalDetailsInput) => Promise<void>, onBack: () => void): HTMLElement {
  const { section, shell } = questionShell('birth-date-title', 'Tell Us About You', 'Share your first name, date of birth, and the details that personalize your reading.');
  if (state.prefilled && state.first_name) shell.append(element('p', { className: 'prefill-note', text: prefillDetailsMessage(state.first_name, Boolean(state.birth_date)) }));

  let birthDate = state.birth_date;
  let gender: PersonalizationGender | undefined = state.gender_personalization;
  let status: BirthTimeStatus | undefined = state.birth_time_status;
  let meridiem: BirthMeridiem | undefined = state.birth_meridiem;

  const form = element('form', { attrs: { novalidate: '' } });
  const name = element('input', { attrs: { id: 'first-name', name: 'first_name', type: 'text', autocomplete: 'given-name', maxlength: '80', required: '', value: state.first_name, placeholder: 'Enter your first name' } });
  const nameField = element('div', { className: 'field' });
  nameField.append(element('label', { text: 'Your first name', attrs: { for: 'first-name' } }), name);

  const genderChoices = element('div', { className: 'focus-list profile-choices', attrs: { role: 'radiogroup', 'aria-label': 'Personalization' } });
  const female = profileChoice('♀', 'Female', 'female');
  const male = profileChoice('♂', 'Male', 'male');
  genderChoices.append(female, male);

  const dateWrap = element('div', { className: 'field birth-date-field' });
  const timeChoices = element('div', { className: 'focus-list birth-time-choices', attrs: { role: 'radiogroup', 'aria-labelledby': 'birth-date-title' } });
  const exact = choice('◷', 'I know my birth time', 'Use hour, minutes, and AM or PM.', 'known');
  const unknown = choice('☽', 'I do not know', 'You can still get a personal reading.', 'unknown');
  timeChoices.append(exact, unknown);

  const timeCard = element('div', { className: 'time-card' });
  const hour = select('birth-hour', 'Hour', ['', ...Array.from({ length: 12 }, (_, index) => String(index + 1))], state.birth_hour?.toString() ?? '');
  const minute = select('birth-minute', 'Minute', ['', ...Array.from({ length: 60 }, (_, index) => String(index).padStart(2, '0'))], state.birth_minute === undefined ? '' : String(state.birth_minute).padStart(2, '0'));
  const am = element('button', { className: 'ampm-btn', text: 'AM', attrs: { type: 'button', 'aria-pressed': String(meridiem === 'AM') } });
  const pm = element('button', { className: 'ampm-btn', text: 'PM', attrs: { type: 'button', 'aria-pressed': String(meridiem === 'PM') } });
  const ampm = element('div', { className: 'ampm-group', attrs: { 'aria-label': 'AM or PM' } });
  ampm.append(am, pm);
  const row = element('div', { className: 'time-row' });
  row.append(hour, minute, ampm);
  timeCard.append(
    element('p', { className: 'field-label', text: 'What time were you born?' }),
    element('p', { className: 'time-helper', text: 'Choose the closest time from your birth certificate or family memory.' }),
    row,
  );

  const error = stepError('birth_date');
  form.setAttribute('aria-describedby',error.id);
  stepError('birth_details');
  stepError('profile');
  const submit = element('button', { className: 'primary', text: 'Continue My Reading', attrs: { type: 'submit' } });

  const candidate = (): PersonalDetailsInput => ({
    first_name: name.value,
    gender_personalization: gender ?? 'female',
    birth_date: birthDate,
    birth_time_status: status ?? 'unknown',
    birth_hour: status === 'known' && hour.value ? Number(hour.value) : undefined,
    birth_minute: status === 'known' && minute.value ? Number(minute.value) : undefined,
    birth_meridiem: status === 'known' ? meridiem : undefined,
    // Historical values remain attached to the profile, but new sessions no longer collect birthplace.
    birthplace: state.birthplace,
  });

  const recompute = () => {
    female.setAttribute('aria-checked', String(gender === 'female'));
    male.setAttribute('aria-checked', String(gender === 'male'));
    [exact, unknown].forEach((button) => button.setAttribute('aria-checked', String(button.dataset.value === status)));
    const known = status === 'known';
    timeCard.classList.toggle('hidden', !known);
    hour.disabled = !known;
    minute.disabled = !known;
    am.disabled = !known;
    pm.disabled = !known;
    am.setAttribute('aria-pressed', String(known && meridiem === 'AM'));
    pm.setAttribute('aria-pressed', String(known && meridiem === 'PM'));

    const values = candidate();
    const valid = typeof gender === 'string'
      && status !== undefined
      && isBirthDateStepValid(values.birth_date)
      && nameSchema.safeParse(values.first_name).success
      && profileInputSchema.safeParse({ first_name: values.first_name, gender_personalization: values.gender_personalization }).success
      && (status === 'unknown' || isBirthDetailsStepValid({
        birth_time_status: status,
        birth_hour: values.birth_hour,
        birth_minute: values.birth_minute,
        birth_meridiem: values.birth_meridiem,
        birthplace: values.birthplace,
      }));
    submit.disabled = !valid;
    error.textContent = '';
  };

  dateWrap.append(
    birthDateSelector(birthDate, (value) => { birthDate = value; recompute(); }),
    element('small', { text: 'Alunar is available to adults aged 18 and over.' }),
  );

  exact.addEventListener('click', () => { status = 'known'; recompute(); });
  unknown.addEventListener('click', () => { status = 'unknown'; hour.value = ''; minute.value = ''; meridiem = undefined; recompute(); });
  hour.addEventListener('change', recompute);
  minute.addEventListener('change', recompute);
  am.addEventListener('click', () => { meridiem = 'AM'; recompute(); });
  pm.addEventListener('click', () => { meridiem = 'PM'; recompute(); });
  female.addEventListener('click', () => { gender = 'female'; recompute(); });
  male.addEventListener('click', () => { gender = 'male'; recompute(); });
  name.addEventListener('input',recompute);
  name.addEventListener('change',recompute);
  name.addEventListener('blur',recompute);

  let lastObservedName = name.value;
  const autofillSync=window.setInterval(() => {
    if (name.value === lastObservedName) return;
    lastObservedName = name.value;
    recompute();
  }, 250);
  registerCleanup(section, () => window.clearInterval(autofillSync));
  recompute();

  form.append(
    nameField,
    element('p', { className: 'field-label', text: 'How should we personalize your reading?' }),
    genderChoices,
    dateWrap,
    timeChoices,
    timeCard,
    error,
    submit,
    backButton('Return', onBack),
  );

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    const submittedName = submittedFirstName(new FormData(form).get('first_name'), name.value);
    const values = candidate();
    const parsedProfile = profileInputSchema.safeParse({ first_name: submittedName, gender_personalization: values.gender_personalization });
    const parsedDate = birthDateSchema.safeParse(values.birth_date);
    const parsedDetails = birthDetailsSchema.safeParse(status === 'known'
      ? { birth_time_status: 'known', birth_hour: values.birth_hour, birth_minute: values.birth_minute, birth_meridiem: values.birth_meridiem, birthplace: values.birthplace }
      : { birth_time_status: 'unknown', birthplace: values.birthplace });

    if (!parsedProfile.success || !parsedDate.success || !parsedDetails.success || !gender || !status) {
      const issue = parsedProfile.error?.issues[0] ?? parsedDate.error?.issues[0] ?? parsedDetails.error?.issues[0] ?? { message: 'Please complete every required field.' };
      error.textContent = issue.message;
      submit.disabled = true;
      return;
    }

    submit.disabled = true;
    error.textContent = '';
    try {
      await onSubmit({
        ...values,
        first_name: parsedProfile.data.first_name,
        gender_personalization: parsedProfile.data.gender_personalization,
        birth_date: parsedDate.data,
        birth_time_status: parsedDetails.data.birth_time_status,
        birth_hour: parsedDetails.data.birth_time_status === 'known' ? parsedDetails.data.birth_hour : undefined,
        birth_minute: parsedDetails.data.birth_time_status === 'known' ? parsedDetails.data.birth_minute : undefined,
        birth_meridiem: parsedDetails.data.birth_time_status === 'known' ? parsedDetails.data.birth_meridiem : undefined,
        birthplace: parsedDetails.data.birthplace,
      });
    } catch (caught) {
      error.textContent = caught instanceof Error ? caught.message : 'Please try again.';
      submit.disabled = !isBirthDateStepValid(birthDate) || !nameSchema.safeParse(submittedName).success || !profileInputSchema.safeParse({ first_name: submittedName, gender_personalization: gender }).success || !isBirthDetailsStepValid({
        birth_time_status: status,
        birth_hour: hour.value ? Number(hour.value) : undefined,
        birth_minute: minute.value ? Number(minute.value) : undefined,
        birth_meridiem: meridiem,
        birthplace: state.birthplace,
      });
    }
  });

  shell.append(form);
  return section;
}

export function birthDetailsView(state: AppState, onSubmit: (details: BirthDetailsInput) => Promise<void>, onBack: () => void): HTMLElement {
  return birthDateView(state, async (details) => {
    await onSubmit({
      birth_time_status: details.birth_time_status,
      birth_hour: details.birth_hour,
      birth_minute: details.birth_minute,
      birth_meridiem: details.birth_meridiem,
      birthplace: details.birthplace,
    });
  }, onBack);
}

export function profileView(state: AppState, onSubmit: (values: { first_name: string; gender_personalization: PersonalizationGender }) => Promise<void>, onBack: () => void): HTMLElement {
  return birthDateView(state, async (details) => {
    await onSubmit({ first_name: details.first_name, gender_personalization: details.gender_personalization });
  }, onBack);
}

export function submittedFirstName(formValue: FormDataEntryValue | null, inputValue: string): string {
  return (typeof formValue === 'string' ? formValue : inputValue).trim();
}

function choice(glyph: string, title: string, copy: string, value: string): HTMLButtonElement {
  const button = element('button', { className: 'focus-option birth-choice', attrs: { type: 'button', role: 'radio', 'aria-checked': 'false' } });
  button.dataset.value = value;
  const text = element('span', { className: 'option-copy' });
  text.append(element('strong', { text: title }), element('small', { text: copy }));
  button.append(element('span', { className: 'opt-glyph', text: glyph, attrs: { 'aria-hidden': 'true' } }), text);
  return button;
}

function select(id: string, label: string, values: string[], selected: string): HTMLSelectElement {
  const input = element('select', { className: 'time-select', attrs: { id, 'aria-label': label } });
  values.forEach((value, index) => input.append(element('option', { text: value || label, attrs: { value, ...(index === 0 ? { disabled: '' } : {}) } })));
  input.value = selected;
  return input;
}

function profileChoice(glyph: string, label: string, value: PersonalizationGender): HTMLButtonElement {
  const button = element('button', { className: 'focus-option', attrs: { type: 'button', role: 'radio', 'aria-checked': 'false' } });
  button.dataset.value = value;
  button.append(element('span', { text: glyph, attrs: { 'aria-hidden': 'true' } }), document.createTextNode(label));
  return button;
}
